import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Edit, Trash2, Search, Plus, BarChart3, Eye, MousePointer, X } from 'lucide-react';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import advertisementApi from '../../services/advertisementApi';
import { useAuth } from '../../contexts/AuthContext';
import useAdminStore from '../../store/adminStore';
import AdvertisementEditModal from './AdvertisementEditModal';
import AdvertisementCreateModal from './AdvertisementCreateModal';
import DeleteModal from '../Common/DeleteModal';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const AdvertisementTable = () => {
  const { t, i18n } = useTranslation('admin');
  const dateLocale = getDateFnsLocale(i18n.language);
  const { authToken } = useAuth();
  const { setLoading, setError, error, loading } = useAdminStore();
  const [advertisements, setAdvertisements] = useState([]);
  const [editingAd, setEditingAd] = useState(null);
  const [deletingAd, setDeletingAd] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAdStats, setSelectedAdStats] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    position: 'all',
    isActive: 'all',
    targetAudience: 'all',
  });

  const loadAdvertisements = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await advertisementApi.getAdvertisements({
        token: authToken,
        filters: {
          position: filters.position !== 'all' ? filters.position : undefined,
          isActive: filters.isActive !== 'all' ? filters.isActive === 'true' : undefined,
          targetAudience: filters.targetAudience !== 'all' ? filters.targetAudience : undefined,
        },
      });
      if (result.success) {
        setAdvertisements(result.advertisements || []);
      } else {
        setError(result.error || t('advertisementTable.loadFailedFallback'));
      }
    } catch (error) {
      setError(getApiErrorMessage(error, { fallback: t('advertisementTable.genericErrorFallback') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      loadAdvertisements();
    }
  }, [authToken, filters.position, filters.isActive, filters.targetAudience]);

  const handleDelete = async () => {
    if (!deletingAd) return;
    setLoading(true);
    try {
      const result = await advertisementApi.deleteAdvertisement({
        token: authToken,
        adId: deletingAd.id,
      });
      if (result.success) {
        setAdvertisements((prev) => prev.filter((ad) => ad.id !== deletingAd.id));
        setDeletingAd(null);
      } else {
        setError(result.error || t('advertisementTable.deleteFailedFallback'));
      }
    } catch (error) {
      setError(getApiErrorMessage(error, { fallback: t('advertisementTable.genericErrorFallback') }));
    } finally {
      setLoading(false);
    }
  };

  const handleViewStats = async (adId) => {
    setLoading(true);
    try {
      const result = await advertisementApi.getAdvertisementStats({
        token: authToken,
        adId,
      });
      if (result.success) {
        setSelectedAdStats({ adId, stats: result.stats });
      } else {
        setError(result.error || t('advertisementTable.statsLoadFailedFallback'));
      }
    } catch (error) {
      setError(getApiErrorMessage(error, { fallback: t('advertisementTable.genericErrorFallback') }));
    } finally {
      setLoading(false);
    }
  };

  const filteredAds = advertisements.filter((ad) => {
    const matchesSearch =
      !filters.search ||
      ad.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
      ad.description?.toLowerCase().includes(filters.search.toLowerCase());
    return matchesSearch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return t('advertisementTable.unlimited');
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: dateLocale });
    } catch {
      return t('advertisementTable.invalidDate');
    }
  };

  const getPositionLabel = (position) => {
    const labels = {
      sidebar: t('advertisementTable.positions.sidebar'),
      header: t('advertisementTable.positions.header'),
      notes_list: t('advertisementTable.positions.notes_list'),
      modal: t('advertisementTable.positions.modal'),
    };
    return labels[position] || position;
  };

  const getTargetAudienceLabel = (audience) => {
    const labels = {
      free: t('advertisementTable.audiences.free'),
      plus: t('advertisementTable.audiences.plus'),
      all: t('advertisementTable.audiences.all'),
    };
    return labels[audience] || audience;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{t('advertisementTable.title')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('advertisementTable.totalCount', { count: filteredAds.length })}
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-[#2563eb] hover:bg-[#3d8ef0] text-white rounded-lg font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>{t('advertisementTable.newAd')}</span>
        </motion.button>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('advertisementTable.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-[#161f31] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
          />
        </div>
        <select
          value={filters.position}
          onChange={(e) => setFilters({ ...filters, position: e.target.value })}
          className="px-4 py-2 bg-white dark:bg-[#161f31] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
        >
          <option value="all">{t('advertisementTable.allPositions')}</option>
          <option value="sidebar">{t('advertisementTable.positions.sidebar')}</option>
          <option value="header">{t('advertisementTable.positions.header')}</option>
          <option value="notes_list">{t('advertisementTable.positions.notes_list')}</option>
          <option value="modal">{t('advertisementTable.positions.modal')}</option>
        </select>
        <select
          value={filters.isActive}
          onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
          className="px-4 py-2 bg-white dark:bg-[#161f31] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
        >
          <option value="all">{t('advertisementTable.allStatuses')}</option>
          <option value="true">{t('advertisementTable.active')}</option>
          <option value="false">{t('advertisementTable.inactive')}</option>
        </select>
        <select
          value={filters.targetAudience}
          onChange={(e) => setFilters({ ...filters, targetAudience: e.target.value })}
          className="px-4 py-2 bg-white dark:bg-[#161f31] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
        >
          <option value="all">{t('advertisementTable.allAudiences')}</option>
          <option value="free">{t('advertisementForm.audiences.free')}</option>
          <option value="plus">Plus</option>
          <option value="all">{t('advertisementTable.audienceAll')}</option>
        </select>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Stats Modal */}
      {selectedAdStats && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-white/10 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('advertisementTable.statsModal.title')}</h3>
              <button
                onClick={() => setSelectedAdStats(null)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1e2d47] text-gray-500 dark:text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#161f31] rounded-lg">
                <div className="flex items-center space-x-2">
                  <Eye className="w-5 h-5 text-[#3d8ef0] dark:text-[#7ab8f8]" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('advertisementTable.statsModal.impressions')}</span>
                </div>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedAdStats.stats.impressions}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#161f31] rounded-lg">
                <div className="flex items-center space-x-2">
                  <MousePointer className="w-5 h-5 text-[#3d8ef0] dark:text-[#7ab8f8]" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('advertisementTable.statsModal.clicks')}</span>
                </div>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedAdStats.stats.clicks}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#161f31] rounded-lg">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('advertisementTable.statsModal.ctr')}</span>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  %{selectedAdStats.stats.clickThroughRate}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#161f31] rounded-lg">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('advertisementTable.statsModal.uniqueUsers')}</span>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedAdStats.stats.uniqueUsers}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-[var(--color-bg-elevated)] rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('advertisementTable.columns.title')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('advertisementTable.columns.position')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('advertisementTable.columns.targetAudience')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('advertisementTable.columns.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('advertisementTable.columns.impressions')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('advertisementTable.columns.clicks')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('advertisementTable.columns.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-white/[0.07]">
              {filteredAds.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    {loading ? t('advertisementTable.loading') : t('advertisementTable.noAdsFound')}
                  </td>
                </tr>
              ) : (
                filteredAds.map((ad) => (
                  <motion.tr
                    key={ad.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 dark:hover:bg-[#1e2d47]/30 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{ad.title}</div>
                      {ad.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                          {ad.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {getPositionLabel(ad.position)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {getTargetAudienceLabel(ad.targetAudience)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          ad.isActive
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                        }`}
                      >
                        {ad.isActive ? t('advertisementTable.active') : t('advertisementTable.inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {ad.currentImpressions || 0}
                        {ad.maxImpressions && ` / ${ad.maxImpressions}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {ad.currentClicks || 0}
                        {ad.maxClicks && ` / ${ad.maxClicks}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleViewStats(ad.id)}
                          className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          title={t('advertisementTable.statsTooltip')}
                        >
                          <BarChart3 className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setEditingAd(ad)}
                          className="p-2 text-[#3d8ef0] dark:text-[#7ab8f8] hover:bg-primary-50 dark:hover:bg-[#2563eb]/10 rounded-lg transition-colors"
                          title={t('advertisementTable.editTooltip')}
                        >
                          <Edit className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setDeletingAd(ad)}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title={t('advertisementTable.deleteTooltip')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {editingAd && (
        <AdvertisementEditModal
          advertisement={editingAd}
          isOpen={!!editingAd}
          onClose={() => setEditingAd(null)}
          onSuccess={loadAdvertisements}
        />
      )}
      {showCreateModal && (
        <AdvertisementCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={loadAdvertisements}
        />
      )}
      {deletingAd && (
        <DeleteModal
          isOpen={!!deletingAd}
          onClose={() => setDeletingAd(null)}
          onConfirm={handleDelete}
          title={t('advertisementTable.deleteModal.title')}
          message={t('advertisementTable.deleteModal.message', { title: deletingAd.title })}
        />
      )}
    </div>
  );
};

export default AdvertisementTable;



